"use client";
import { Children, isValidElement, type JSX } from "react";
import BaseHomepageHeroMarketplace from "./BaseHomepageHeroMarketplace";
import type { IconTypes } from "@/components/icons/types";
import type { TagColor } from "@/components/Tags/types";

export type ShortCodeMarketplaceProps = {
  // Basic content
  title?: string;
  title_text_color?: string;
  description_item?: string;
  description_text_color?: string;
  theme?: "light" | "dark";

  // Images
  image_url_desktop?: string;
  image_url_tablet?: string;
  image_url_mobile?: string;

  // CTAs
  primary_cta_text?: string;
  primary_cta_link?: string;
  secondary_cta_text?: string;
  secondary_cta_link?: string;

  // Badges (not in carousel spec but used by marketplace component)
  badge_icon?: string;
  badge_text?: string;
  badge_color?: string;
  badge_borderless?: string;

  // Testimonial
  testimonial_quote?: string;
  testimonial_author?: string;
  testimonial_text_color?: string;

  // Stat
  stat_icon?: string;
  stat_value?: string;
  stat_label?: string;
  stat_source_name?: string;
  stat_source_link?: string;
  stat_text_color?: string;

  // Search
  search_placeholder?: string;
  search_button_color?: string;
  search_button_label?: string;
  search_button_label_color?: string;

  // Eventing
  section_html_id?: string;
  section_location?: string;
  section_title?: string;
  primary_cta_html_id?: string;
  primary_cta_name?: string;
  secondary_cta_html_id?: string;
  secondary_cta_name?: string;

  // Additional props
  heading_level?: "h1" | "h2";
  class_name?: string;
  is_carousel_slide?: string;
};

export default function ShortCodeMarketplace({
  title,
  title_text_color,
  description_item,
  description_text_color,
  theme = "dark",
  image_url_desktop,
  image_url_tablet,
  image_url_mobile,
  primary_cta_text,
  primary_cta_link,
  secondary_cta_text,
  secondary_cta_link,
  badge_icon,
  badge_text,
  badge_color,
  badge_borderless = "false",
  testimonial_quote,
  testimonial_author,
  testimonial_text_color,
  stat_icon,
  stat_value,
  stat_label,
  stat_source_name,
  stat_source_link,
  stat_text_color,
  search_placeholder,
  search_button_color,
  search_button_label,
  search_button_label_color,
  section_html_id,
  section_location,
  section_title,
  primary_cta_html_id,
  primary_cta_name,
  secondary_cta_html_id,
  secondary_cta_name,
  heading_level = "h1",
  class_name = "",
  is_carousel_slide = "false",
}: ShortCodeMarketplaceProps) {
  // Build CTA objects
  const primaryCta =
    primary_cta_link && primary_cta_text
      ? {
          url: primary_cta_link,
          label: primary_cta_text,
        }
      : undefined;

  const secondaryCta =
    secondary_cta_link && secondary_cta_text
      ? {
          url: secondary_cta_link,
          label: secondary_cta_text,
        }
      : undefined;

  // Build badges array
  const badges =
    badge_text && badge_color
      ? [
          {
            icon: badge_icon as IconTypes,
            text: badge_text,
            color: badge_color as TagColor,
            borderless: badge_borderless === "true",
          },
        ]
      : undefined;

  // Build testimonial object
  const testimonial =
    testimonial_quote && testimonial_author && testimonial_text_color
      ? {
          quote: testimonial_quote,
          author: testimonial_author,
          textColor: testimonial_text_color,
        }
      : undefined;

  // Build stat object
  const stat =
    stat_value && stat_label && stat_text_color
      ? {
          icon: stat_icon as IconTypes,
          value: stat_value,
          label: stat_label,
          sourceName: stat_source_name,
          sourceLink: stat_source_link,
          textColor: stat_text_color,
        }
      : undefined;

  // Build description items array
  const descriptionItems = description_item ? [description_item] : undefined;

  // Build search object
  const searchConfig =
    search_placeholder || search_button_label
      ? {
          placeholder: search_placeholder,
          searchButtonColor: search_button_color,
          searchButtonLabel: search_button_label,
          searchButtonLabelColor: search_button_label_color,
        }
      : undefined;

  // Build eventing object
  const eventing =
    section_html_id ||
    section_location ||
    section_title ||
    primary_cta_html_id ||
    primary_cta_name ||
    secondary_cta_html_id ||
    secondary_cta_name
      ? {
          sectionHtmlId: section_html_id,
          sectionLocation: section_location,
          sectionTitle: section_title,
          primaryCtaHtmlId: primary_cta_html_id,
          primaryCtaName: primary_cta_name,
          secondaryCtaHtmlId: secondary_cta_html_id,
          secondaryCtaName: secondary_cta_name,
        }
      : undefined;

  return (
    <BaseHomepageHeroMarketplace
      title={title}
      titleColor={title_text_color}
      descriptionItems={descriptionItems}
      descriptionColor={description_text_color}
      theme={theme}
      desktopImage={image_url_desktop}
      tabletImage={image_url_tablet}
      mobileImage={image_url_mobile}
      primaryCta={primaryCta}
      secondaryCta={secondaryCta}
      badges={badges}
      testimonial={testimonial}
      stat={stat}
      search={searchConfig}
      eventing={eventing}
      headingLevel={heading_level}
      className={class_name}
      isCarouselSlide={is_carousel_slide === "true"}
    />
  );
}
